/**
 * Engine builder — nacelle cylinders and optional propeller discs.
 * Placement varies by category: nose, wing-mounted, or rear-fuselage.
 */

import * as THREE from 'three'
import type { CategoryProfile } from '../types'
import { getPropDiscMaterial } from '../materials'

const ENGINE_SEGMENTS = 8

export function createEngines(
  profile: CategoryProfile,
  fuselageRadius: number,
  fuselageLength: number,
  wingspan: number,
  wingY: number,
  wingZ: number,
  material: THREE.MeshStandardMaterial
): THREE.Group {
  const group = new THREE.Group()
  group.name = 'engines'

  const nacelleRadius = fuselageRadius * profile.engineDiameterRatio / 2
  const nacelleLength = nacelleRadius * 4 // reasonable nacelle proportions

  if (profile.enginePlacement === 'nose') {
    // Single engine at nose (piston aircraft)
    const nacelle = createNacelle(nacelleRadius, nacelleLength, material)
    nacelle.position.set(0, 0, fuselageLength / 2 + nacelleLength * 0.3)
    group.add(nacelle)

    if (profile.hasPropDisc) {
      const disc = createPropDisc(fuselageRadius * 1.1)
      disc.position.set(0, 0, fuselageLength / 2 + nacelleLength * 0.6)
      group.add(disc)
    }
  } else if (profile.enginePlacement === 'wing') {
    // Two engines mounted under/on wings at ~30% semi-span
    const engineSpanOffset = wingspan * 0.15
    const engineZ = wingZ - nacelleLength * 0.3 // slightly behind wing leading edge

    for (const side of [-1, 1] as const) {
      const nacelle = createNacelle(nacelleRadius, nacelleLength, material)
      nacelle.position.set(
        side * engineSpanOffset,
        wingY - nacelleRadius * 1.2, // below wing
        engineZ
      )
      group.add(nacelle)

      if (profile.hasPropDisc) {
        const disc = createPropDisc(nacelleRadius * 2.5)
        disc.position.set(
          side * engineSpanOffset,
          wingY - nacelleRadius * 1.2,
          engineZ + nacelleLength / 2 + nacelleRadius * 0.3
        )
        group.add(disc)
      }
    }
  } else if (profile.enginePlacement === 'rear_fuselage') {
    // Two engines on rear fuselage sides (business jet style)
    const engineZ = -fuselageLength * 0.35
    const engineX = fuselageRadius * 1.1

    for (const side of [-1, 1] as const) {
      const nacelle = createNacelle(nacelleRadius, nacelleLength, material)
      nacelle.position.set(
        side * engineX,
        fuselageRadius * 0.3, // slightly above centerline
        engineZ
      )
      group.add(nacelle)
    }
  }

  return group
}

function createNacelle(
  radius: number,
  length: number,
  material: THREE.MeshStandardMaterial
): THREE.Mesh {
  const geo = new THREE.CylinderGeometry(radius, radius, length, ENGINE_SEGMENTS)
  const mesh = new THREE.Mesh(geo, material)
  mesh.rotation.x = Math.PI / 2 // align along Z axis
  mesh.castShadow = true
  return mesh
}

function createPropDisc(radius: number): THREE.Mesh {
  const geo = new THREE.CircleGeometry(radius, 16)
  const mesh = new THREE.Mesh(geo, getPropDiscMaterial())
  // Circle faces +Z by default — correct for nose-mounted props
  mesh.castShadow = false
  mesh.receiveShadow = false
  return mesh
}
